import {
    EmptyCloudComponentLoader,
    ICloudComponentsLoader
} from '@mintdata-wsp/common/controllers/artifacts/CloudComponentsLoader';
import {CloudTypesManager} from '@mintdata-wsp/common/controllers/artifacts/CloudTypesManager';
import {VisualComponentDef, VisualComponentDefs} from '@mintdata-wsp/common/controllers/artifacts/VisualComponentDefs';
import {IVisualComponentsLoader} from '@mintdata-wsp/common/controllers/artifacts/VisualComponentsLoader';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {ResolvedVisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/ResolvedVisualComponent';
import {VisualArtifact} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualArtifact';
import {VisualArtifactRef} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualArtifactRef';
import {
    VisualComponent,
    VisualComponentId
} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {VisualComponentRef} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponentRef';
import {IBoxSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {IPageSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {ISurfaceStateSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {
    ArtifactsState,
    CloudComponent,
    CloudComponentGroup,
    CloudPort,
    ResolvedCloudArtifact
} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {action, runInAction} from 'mobx';
import * as React from 'react';

export class ArtifactsController
{
    private readonly _cloudTypesManager:CloudTypesManager = new CloudTypesManager();

    constructor(/**private readonly _vcLoader:IVisualComponentsLoader,**/
                private readonly _cloudLoader:ICloudComponentsLoader = new EmptyCloudComponentLoader())
    {
    }

    public getCloudTypesManager():CloudTypesManager
    {
        return this._cloudTypesManager;
    }

    // public get visualComponentsLoader():IVisualComponentsLoader
    // {
    //     return this._vcLoader;
    // }

    public ensureCloudArtifactsAreLoaded():Promise<boolean>
    {
        const cloudArtifacts = this.getCloudArtifacts();
        return cloudArtifacts
            ? Promise.resolve(true)
            : this.loadCloudArtifacts();

    }

    public loadCloudArtifacts():Promise<boolean>
    {
        return this._cloudLoader.loadAndResolveAllCloudArtifacts()
            .then((cloudArtifacts:ResolvedCloudArtifact[]) =>
            {
                const filteredArtifacts = this._filterCloudArtifacts(
                    cloudArtifacts,
                    [
                        'mintdata.components:logical-components:0.3.0-SNAPSHOT',
                        'mintdata.components:io-components:0.3.0-SNAPSHOT'
                        // 'mintdata.components:logical-components:0.3.0',
                        // 'mintdata.components:io-components:0.3.0'
                    ]);

                this.setCloudArtifacts(filteredArtifacts);
                return true;
            });
    }

    public _filterCloudArtifacts(cloudArtifacts:ResolvedCloudArtifact[],
                                 mvnArtifactDescriptors:string[]):ResolvedCloudArtifact[]
    {
        const filteredCloudArtifacts = cloudArtifacts.filter((rca:ResolvedCloudArtifact) =>
        {
            const rcaDescriptor = rca.getMavenDescriptor();

            for( let desc of mvnArtifactDescriptors )
            {
                if( desc === rcaDescriptor )
                    return true;
            }

            return false;
        });

        return filteredCloudArtifacts;
    }

    // @action
    // public loadVisualArtifacts(visualAritfactsRefs?:Array<VisualArtifactRef>):Promise<boolean>
    // {
    //     const state:ArtifactsState = this._getArtifactsState();
    //
    //     if( !state.visualArtifactsLoadingPromise )
    //         state.visualArtifactsLoadingPromise = this._vcLoader.loadVisualArtifacts(visualAritfactsRefs)
    //             .then((visualArtifacts:VisualArtifact[]) =>
    //             {
    //                 this.setVisualArtifacts(visualArtifacts);
    //                 return true;
    //             });
    //
    //     return state.visualArtifactsLoadingPromise;
    // }

    // @bind
    // public async resolveVisualComponent(visualComponent:VisualComponent):Promise<ResolvedVisualComponent>
    // {
    //     if( !visualComponent.ref.resolveComponentPromise )
    //     {
    //         const sjsModule:any = await this._vcLoader.getSystemJSInstance(
    //             this.getVisualArtifacts()
    //         ).import(visualComponent.ref.definedBy);
    //
    //         const reactClassCtor:React.ComponentClass<any> = sjsModule[visualComponent.ref.exportName];
    //
    //         runInAction(() =>
    //         {
    //             visualComponent.ref.resolvedComponent = new ResolvedVisualComponent(reactClassCtor);
    //         });
    //     }
    //
    //     return visualComponent.ref.resolvedComponent;
    // }

    @bind
    public async resolveVisualComponents(surfaceSnapshot:ISurfaceStateSnapshot):Promise<any>
    {
        const promises:Array<Promise<any>> = [];

        surfaceSnapshot && Object.values(surfaceSnapshot.pages).forEach((page:IPageSnapshot) =>
        {
            Object.values(page.artboard.allBoxes).forEach((box:IBoxSnapshot) =>
            {
                const component:VisualComponent = rootState.ui.visualDesigner.artifacts.visualComponents.get(box.componentId);

                promises.push(this.resolveVisualComponent(component));
            });
        });

        return Promise.all(promises);
    }

    public async startLoadingBundlesForVisualArtifacts():Promise<Array<ResolvedVisualComponent>>
    {
        const promises:Array<Promise<ResolvedVisualComponent>> = [];
        const visualArtifacts = this.getVisualArtifacts();

        visualArtifacts.forEach((va:VisualArtifact) =>
            va.visualComponents.forEach((vc:VisualComponent) =>
            {
                promises.push(this.resolveVisualComponent(vc));
            })
        );

        return Promise.all(promises);
    }

    public getBakeryComponentByName(name:string):CloudComponent | null
    {
        return this._getBakeryComponent(name);
    }

    public getBakeryComponentFor(visualComponent:VisualComponent):CloudComponent | null
    {
        return this._getBakeryComponent(visualComponent.name);
    }

    private _getBakeryComponent(visualComponentName:string)
    {
        let groupId = IdUtil.newGUID();
        let cloudComponentGroup = new CloudComponentGroup(groupId, 'Bakery Component Artifact', 'Bakery Component Artifact', 'Bakery');
        let clazz = 'mintdata.visual.component.BakeryComponentAdapter';

        let inputPort = new CloudPort('data', '', false);
        let outputPort = new CloudPort('uiEvent', '', false);

        return new CloudComponent(cloudComponentGroup, clazz, visualComponentName, '', [], [], [], [inputPort], [outputPort]);
    }

    public getCloudComponentByName(name:string):CloudComponent | null
    {
        const allCloudComponents = this.getAllCloudComponents();

        return allCloudComponents.find((component:CloudComponent) => component.name === name);
    }

    public getCloudArtifacts():ResolvedCloudArtifact[] | null
    {
        return this._getArtifactsState().cloudArtifacts;
    }

    @action.bound
    public setCloudArtifacts(cloudArtifacts:ResolvedCloudArtifact[]):void
    {
        const artifacts = this._getArtifactsState();
        artifacts.cloudArtifacts = cloudArtifacts;
    }

    public static toAllCloudComponents(cloudArtifacts:ResolvedCloudArtifact[]):CloudComponent[] | null
    {
        if( !cloudArtifacts )
            return null;

        let allCloudComponents = [];
        cloudArtifacts.map((rca:ResolvedCloudArtifact) =>
        {
            allCloudComponents = allCloudComponents.concat(rca.components);
        });

        return allCloudComponents;
    }

    public getAllCloudComponents():CloudComponent[] | null
    {
        const state = this._getArtifactsState();
        const allCloudArtifacts = state.cloudArtifacts;

        return ArtifactsController.toAllCloudComponents(allCloudArtifacts);
    }

    public getVisualArtifacts():VisualArtifact[] | null
    {
        return this._getArtifactsState().visualArtifacts;
    }

    @action.bound
    public setVisualArtifacts(visualArtifacts:VisualArtifact[]):void
    {
        const artifacts = this._getArtifactsState();
        artifacts.visualArtifacts = visualArtifacts;

        visualArtifacts.forEach((visualArtifact:VisualArtifact) =>
        {
            visualArtifact.visualComponents.forEach((visualComponent:VisualComponent) =>
            {
                artifacts.visualComponents.set(visualComponent.id, visualComponent);
            });
        });
    }

    @action
    public resetArtifactsLoadingPromise():void
    {
        const state = this._getArtifactsState();
        state.visualArtifactsLoadingPromise = null;
    }

    public getVisualComponents():Map<VisualComponentId, VisualComponent>
    {
        return rootState.ui.visualDesigner.artifacts.visualComponents;
    }

    private _getArtifactsState():ArtifactsState
    {
        return rootState.ui.visualDesigner.artifacts;
    }

    // New way of loading visual components (use of code-splitting)

    @action
    public async loadVisualArtifacts():Promise<boolean>
    {
        const visualArtifactRef:VisualArtifactRef = ConfigUtil.latestVisualArtifacts()
            .find(({name}) => name === 'mint-components');

        const visualComponents:VisualComponent[] = VisualComponentDefs
            .map((def:VisualComponentDef) =>
            {
                const componentRef = new VisualComponentRef(
                    VisualComponent.idFrom(visualArtifactRef, def.name),
                    def.definedBy,
                    def.name
                );
                return new VisualComponent(visualArtifactRef, componentRef, def.name, def.tip, def.getReactClassCtor);
            });

        const visualArtifacts:VisualArtifact[] = [
            new VisualArtifact(visualArtifactRef, visualComponents)
        ];

        this.setVisualArtifacts(visualArtifacts);

        return true;
    }

    @bind
    public async resolveVisualComponent(visualComponent:VisualComponent):Promise<ResolvedVisualComponent>
    {
        if( visualComponent.ref.isResolved )
            return visualComponent.ref.resolvedComponent;

        if( !visualComponent.ref.resolveComponentPromise )
            visualComponent.ref.resolveComponentPromise = this._resolveVisualComponent(visualComponent);

        return await visualComponent.ref.resolveComponentPromise;
    }

    @bind
    private async _resolveVisualComponent(visualComponent:VisualComponent):Promise<ResolvedVisualComponent>
    {
        const reactClassCtor:React.ComponentClass<any> = await visualComponent.getReactClassCtor();

        runInAction(() => visualComponent.ref.resolvedComponent = new ResolvedVisualComponent(reactClassCtor));

        return visualComponent.ref.resolvedComponent;
    }
}
